/**
 * HmOrderDetail — formatka zamówienia (PODGLĄD).
 *
 * Bez przycisku wydruku: WebAPI Symfonii nie udostępnia PDF-a dla zamówień. To brak po stronie
 * Symfonii, nie nasza zaległość — dlatego przycisku nie ma wcale zamiast wyłączonego.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { HmOrderDetail as HmOrderDetailDto } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import {
  AddressBlock,
  day,
  DetailError,
  DetailHeader,
  DetailTabBody,
  DetailTabs,
  Field,
  Section,
  TotalsBar,
  useDocumentTabLabel,
} from '@/components/handel-magazyn/detail-parts';
import { HmApprovalSection } from '@/components/handel-magazyn/HmApprovalSection';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const TABS = ['positions', 'address', 'extra'] as const;
type Tab = (typeof TABS)[number];

export function HmOrderDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const companyId = useActiveCompanyScope().companyId;
  const [tab, setTab] = useState<Tab>('positions');

  const query = useQuery({
    queryKey: ['handel-magazyn', 'order', id, companyId],
    enabled: !!id && !!companyId,
    retry: false,
    queryFn: async (): Promise<HmOrderDetailDto> => {
      const res = await api.get<HmOrderDetailDto>(
        `/handel-magazyn/orders/${id}?companyId=${companyId}`,
      );
      return res.data;
    },
  });

  // Numer dokumentu znamy dopiero po odpowiedzi — podmieniamy etykiete karty
  // z ogolnej na konkretny numer.
  useDocumentTabLabel(`/handel-magazyn/zamowienia/${id}`, query.data?.documentNumber);

  if (query.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>;
  }
  if (query.error) return <DetailError error={query.error} />;

  const doc = query.data!;

  return (
    <div className="flex h-full flex-col p-4">
      <DetailHeader documentNumber={doc.documentNumber} />

      {/* Obieg akceptacji — patrz `HmApprovalSection`: dokument żyje w Symfonii, u nas powstaje
          wyłącznie rezerwacja identyfikatora, i to dopiero przy wysłaniu do akceptacji.
          Zamówienia są u nas tylko do podglądu, ale akceptować je wolno — obieg niczego
          w Symfonii nie zmienia. */}
      <div className="mb-3">
        <HmApprovalSection kind="order" externalId={doc.id} companyId={companyId} />
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.detail.documentType')}>
            <Field label={t('tenant.handelMagazyn.columns.typeCode')} value={doc.typeCode} />
            <Field label={t('tenant.handelMagazyn.detail.series')} value={doc.series} />
            <Field
              label={t('tenant.handelMagazyn.columns.status')}
              value={
                doc.inBuffer
                  ? t('tenant.handelMagazyn.status.buffer')
                  : t('tenant.handelMagazyn.status.issued')
              }
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.columns.contractor')}>
            <Field label={t('tenant.handelMagazyn.detail.name')} value={doc.buyerName} />
            <Field
              label={t('tenant.handelMagazyn.detail.taxId')}
              value={doc.buyerAddress?.taxId ?? null}
            />
          </Section>
        </div>

        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.detail.payment')}>
            <Field label={t('tenant.handelMagazyn.detail.currency')} value={doc.currency} />
            <Field
              label={t('tenant.handelMagazyn.detail.settled')}
              value={
                doc.settled
                  ? t('tenant.handelMagazyn.detail.yes')
                  : t('tenant.handelMagazyn.detail.no')
              }
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.detail.dates')}>
            <Field label={t('tenant.handelMagazyn.columns.issueDate')} value={day(doc.issueDate)} />
            <Field label={t('tenant.handelMagazyn.columns.saleDate')} value={day(doc.saleDate)} />
            <Field
              label={t('tenant.handelMagazyn.columns.maturityDate')}
              value={day(doc.maturityDate)}
            />
          </Section>
        </div>
      </div>

      <DetailTabs tabs={TABS} active={tab} onChange={setTab} />

      <DetailTabBody>
        {tab === 'positions' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.no')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.code')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.description')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.quantity')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.unit')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.vat')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.price')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.columns.net')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.columns.gross')}
                </th>
              </tr>
            </thead>
            <tbody>
              {doc.positions.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-2 py-6 text-center text-text-tertiary">
                    {t('tenant.handelMagazyn.detail.noPositions')}
                  </td>
                </tr>
              )}
              {doc.positions.map((p) => (
                <tr key={p.id} className="border-b border-border-primary/60">
                  <td className="px-2 py-1.5 text-text-tertiary">{p.no}</td>
                  <td className="px-2 py-1.5 font-mono text-xs text-text-primary">
                    {p.productCode}
                  </td>
                  <td className="px-2 py-1.5 text-text-primary">{p.description}</td>
                  <td className="px-2 py-1.5 text-right text-text-primary">{p.quantity}</td>
                  <td className="px-2 py-1.5 text-text-secondary">{p.unit}</td>
                  <td className="px-2 py-1.5 text-text-secondary">{p.vatRateCode ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right text-text-secondary">{p.priceValue}</td>
                  <td className="px-2 py-1.5 text-right text-text-primary">{p.netValue}</td>
                  <td className="px-2 py-1.5 text-right font-medium text-text-primary">
                    {p.grossValue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'address' && (
          <div className="grid gap-6 md:grid-cols-2">
            <AddressBlock
              title={t('tenant.handelMagazyn.detail.buyerAddress')}
              address={doc.buyerAddress}
            />
          </div>
        )}

        {tab === 'extra' && (
          <div className="space-y-2">
            <Field label={t('tenant.handelMagazyn.detail.description')} value={doc.description} />
            <Field label={t('tenant.handelMagazyn.detail.note')} value={doc.note} />
          </div>
        )}
      </DetailTabBody>

      <TotalsBar
        items={[
          { label: t('tenant.handelMagazyn.columns.net'), value: doc.netValue },
          { label: t('tenant.handelMagazyn.columns.vat'), value: doc.vatValue },
          {
            label: t('tenant.handelMagazyn.columns.gross'),
            value: `${doc.grossValue} ${doc.currency}`,
            strong: true,
          },
        ]}
      />
    </div>
  );
}

export default HmOrderDetail;
